import { captureException } from "@/integrations/sentry";
import { logger } from "@/lib/logging";
import { classifyError, sanitizeObservabilityText } from "@/lib/observability/redaction";
import type { VoiceStatus } from "@/lib/voice/conversation";

/**
 * Voice failures follow the repository's safe-failure doctrine: the raw error
 * goes to the structured logger for diagnosis, and the operator gets a stable
 * sentence they can act on. Messages we construct ourselves (edge-function
 * gates, transport refusals) are curated and pass through; unknown failures
 * collapse to a generic sentence so provider internals never reach the screen.
 */

/** Where in the session lifecycle a voice failure occurred. */
export type VoiceFailureStage = "handshake" | "microphone" | "mint" | "transport" | "unsupported";

/**
 * Carries a message that was written for an operator to read. Only these pass
 * through to the UI — an arbitrary `Error.message` from the network stack, the
 * backend SDK, or a proxy can contain URLs, host names, or stack fragments, so
 * it is logged and replaced rather than rendered (CWE-209).
 */
export class OperatorFacingError extends Error {
  /** Start-sequence stage this failure belongs to, when the thrower knows it. */
  readonly failureStage?: VoiceFailureStage;

  constructor(message: string, failureStage?: VoiceFailureStage) {
    super(message);
    this.name = "OperatorFacingError";
    this.failureStage = failureStage;
  }
}

/**
 * Stage a start-sequence rejection belongs to. Errors that never declared one
 * — provider connect rejections, the connect deadline — failed while the
 * handshake was being negotiated, so that is the default.
 */
export function voiceFailureStageOf(error: unknown): VoiceFailureStage {
  return error instanceof OperatorFacingError && error.failureStage
    ? error.failureStage
    : "handshake";
}

/** Session state reported with a voice failure. Structured fields only. */
export interface VoiceFailureState {
  /** Edge function that mints this provider's session; an app-owned id. */
  adapter: string;
  /** Whether the voice surface had already unmounted when the failure landed. */
  disposed: boolean;
  /** Conversation status the failure interrupted. */
  status: VoiceStatus;
  /** Whether an open provider transport existed at the time. */
  transportOpen: boolean;
}

/**
 * Reports a voice failure to error tracking with structured context only. The
 * captured event carries a stable synthetic message, the failure stage as a
 * searchable tag, and the session state plus `classifyError` dimensions as
 * context — never the raw error, whose message can embed hosts, URLs, or stack
 * fragments (CWE-209). The raw error still reaches the structured logger at
 * the failure site for diagnosis.
 */
export function reportVoiceFailure(
  stage: VoiceFailureStage,
  session: VoiceFailureState,
  cause?: unknown,
  closeDetail?: VoiceCloseDetail,
): void {
  captureException(new Error(`Voice session failed: ${stage}`), {
    contexts: {
      voice_session: {
        ...session,
        ...(cause === undefined ? {} : classifyError(cause)),
      },
      ...(closeDetail ? { voice_close: { ...closeDetail } } : {}),
    },
    ...(closeDetail && isTransientVoiceClose(closeDetail) ? { level: "warning" as const } : {}),
    tags: {
      voice_failure_stage: stage,
      ...(closeDetail ? { voice_close_code: closeDetail.code } : {}),
    },
  });
}

/**
 * Why a provider transport closed, reduced to safe structured fields. The
 * reason is provider-authored text, so it is sanitized and truncated before
 * it can reach error tracking (CWE-209).
 */
export interface VoiceCloseDetail {
  /** WebSocket close code (1006 abnormal, 1008 policy violation, ...). */
  code: number;
  /** Sanitized, truncated provider close reason; empty when none was sent. */
  reason: string;
  /** Milliseconds the socket was open before it closed. */
  msSinceOpen: number;
  /** Whether the session requested a TTS voice override at initiation. */
  voiceOverride: boolean;
  wasClean: boolean;
}

const CLOSE_REASON_LIMIT = 120;
/** A session that ran this long before an abnormal drop was healthy. */
const HEALTHY_SESSION_MS = 10_000;
export const WS_ABNORMAL_CLOSURE = 1006;
export const WS_POLICY_VIOLATION = 1008;

/** Builds a close detail from a `CloseEvent`, sanitizing the provider reason. */
export function voiceCloseDetail(
  event: Pick<CloseEvent, "code" | "reason" | "wasClean">,
  openedAt: number,
  voiceOverride: boolean,
  now: number = Date.now(),
): VoiceCloseDetail {
  return {
    code: event.code,
    msSinceOpen: Math.max(0, now - openedAt),
    reason: sanitizeObservabilityText(event.reason ?? "").slice(0, CLOSE_REASON_LIMIT),
    voiceOverride,
    wasClean: event.wasClean,
  };
}

/** A network drop after a healthy session, as opposed to a provider refusal. */
export function isTransientVoiceClose(detail: VoiceCloseDetail): boolean {
  return detail.code === WS_ABNORMAL_CLOSURE && detail.msSinceOpen >= HEALTHY_SESSION_MS;
}

function errorName(error: unknown): string {
  return error instanceof Error ? error.name : "";
}

const CLIENT_ERROR_MIN = 400;
const CLIENT_ERROR_MAX = 499;

export function readStatusCode(error: unknown): number | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const status = (error as { statusCode?: unknown }).statusCode;
  return typeof status === "number" ? status : null;
}

function readMessage(error: unknown): string {
  if (typeof error !== "object" || error === null) {
    return "";
  }
  const message = (error as { message?: unknown }).message;
  return typeof message === "string" ? message.trim() : "";
}

/**
 * Chooses what the operator sees when the credential mint is refused.
 *
 * Our edge functions answer a rejected request with a 4xx and a sentence we
 * wrote — the staff gate's "Staff profile required", for instance — and that is
 * worth showing. Anything else (5xx, a gateway, the network stack, the backend
 * SDK) carries text we did not author and may embed hosts, URLs, or stack
 * fragments, so the caller's own sentence is used instead (CWE-209).
 */
export function operatorMessageForMintError(error: unknown, fallback: string): string {
  const status = readStatusCode(error);
  const message = readMessage(error);
  const isCurated = status !== null && status >= CLIENT_ERROR_MIN && status <= CLIENT_ERROR_MAX;

  return isCurated && message ? message : fallback;
}

/** Maps `getUserMedia` rejections to guidance an operator can act on. */
export function describeMicrophoneError(error: unknown): string {
  logger.error({ err: error, scope: "voice.microphone" }, "microphone unavailable");

  switch (errorName(error)) {
    case "NotAllowedError":
    case "SecurityError":
      return "Microphone access is blocked. Allow the microphone for this site in your browser settings, then try again.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "No microphone was found. Connect an input device and try again.";
    case "NotReadableError":
      return "The microphone is already in use by another application.";
    default:
      return "The microphone could not be started.";
  }
}

/** Produces a user-facing message for a failed session or negotiation step. */
export function describeConnectionError(error: unknown): string {
  logger.error({ err: error, scope: "voice.connection" }, "voice session failed");

  if (error instanceof OperatorFacingError && error.message.trim()) {
    return error.message;
  }
  return "The voice assistant could not connect. Check your network and try again.";
}
